import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronDown, Check, X, RefreshCw } from 'lucide-react';
import { ModelPicker } from '@/components/superadmin/ModelPicker';
import { useLlmProviders, type LlmProviderSafe, type TestResult, type LiveModel } from '@/hooks/useLlmProviders';
import { LLM_PRESETS, type PresetKey } from '@/lib/llmPresets';
import { useToast } from '@/hooks/use-toast';

interface LlmProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: LlmProviderSafe;
  onSaved: () => void;
}

type Overrides = { english: string; fast: string };

const EMPTY_OVERRIDES: Overrides = { english: '', fast: '' };

export function LlmProviderDialog({ open, onOpenChange, provider, onSaved }: LlmProviderDialogProps) {
  const { toast } = useToast();
  const { testProvider, saveProvider, listModels } = useLlmProviders();

  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState<PresetKey>('groq');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [modelOverrides, setModelOverrides] = useState<Overrides>(EMPTY_OVERRIDES);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowSaveWithoutTest, setAllowSaveWithoutTest] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [liveModels, setLiveModels] = useState<LiveModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // Reset form whenever the dialog opens or the target provider changes.
  useEffect(() => {
    if (!open) return;
    setLabel(provider?.label ?? '');
    const initialPreset = (provider?.preset as PresetKey) ?? 'groq';
    setPreset(initialPreset);
    // New provider: start from the preset so the URL and model are never blank.
    setBaseUrl(provider?.base_url ?? LLM_PRESETS[initialPreset].baseUrl);
    setApiKey('');
    setDefaultModel(provider?.default_model ?? LLM_PRESETS[initialPreset].suggestedModel);
    setLiveModels([]);
    setModelOverrides({
      english: provider?.model_overrides?.english ?? '',
      fast: provider?.model_overrides?.fast ?? '',
    });
    setTestResult(null);
    setTesting(false);
    setSaving(false);
    setAllowSaveWithoutTest(false);
    setAdvancedOpen(false);
  }, [open, provider]);

  const handlePresetChange = (value: string) => {
    const key = value as PresetKey;
    setPreset(key);
    // Switching provider replaces the URL and model: the old ones belong to the old provider.
    setBaseUrl(LLM_PRESETS[key].baseUrl);
    setDefaultModel(LLM_PRESETS[key].suggestedModel);
    setModelOverrides(EMPTY_OVERRIDES);
    setLiveModels([]);
    setTestResult(null);
    setAllowSaveWithoutTest(false);
  };

  // Any edit to a connection-defining field invalidates a prior test.
  const invalidateTest = () => {
    setTestResult(null);
    setAllowSaveWithoutTest(false);
  };

  const handleLoadModels = async () => {
    setLoadingModels(true);
    try {
      const list = await listModels({ id: provider?.id, baseUrl, apiKey: apiKey || undefined });
      setLiveModels(list);
      toast({ title: `${list.length} models available${list.some((m) => m.free) ? ` (${list.filter((m) => m.free).length} free)` : ''}` });
    } catch (e) {
      toast({ title: 'Could not load models', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setLoadingModels(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testProvider({
        id: provider?.id,
        baseUrl,
        apiKey: apiKey || undefined,
        defaultModel,
      });
      setTestResult(result);
      if (result.ok) {
        toast({ title: `Connection OK — ${result.httpStatus} in ${result.latencyMs} ms` });
      } else {
        toast({
          title: 'Connection test failed',
          description: `${result.httpStatus} ${result.error ?? ''}`.trim(),
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({ title: 'Connection test failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (skipTest: boolean) => {
    setSaving(true);
    try {
      await saveProvider({
        id: provider?.id,
        label,
        preset,
        baseUrl,
        apiKey: apiKey || undefined,
        defaultModel,
        modelOverrides,
        testToken: testResult?.testToken,
        skipTest: skipTest || undefined,
      });
      toast({ title: provider ? `Updated ${label}` : `Added ${label}` });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWithoutTesting = () => {
    if (!window.confirm('Save this provider without a successful connection test? It may fail at request time.')) {
      return;
    }
    setAllowSaveWithoutTest(true);
    void handleSave(true);
  };

  const saveDisabled = saving || testing || !label.trim() || (!testResult?.ok && !allowSaveWithoutTest);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{provider ? 'Edit LLM Provider' : 'Add LLM Provider'}</DialogTitle>
          <DialogDescription>
            Configure an OpenAI-compatible endpoint. Test the connection before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="llm-label">Label</Label>
            <Input
              id="llm-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Groq production"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-preset">Preset</Label>
            <Select value={preset} onValueChange={handlePresetChange}>
              <SelectTrigger id="llm-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(LLM_PRESETS) as PresetKey[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {LLM_PRESETS[key].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-base-url">Base URL</Label>
            <Input
              id="llm-base-url"
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value.trim());
                invalidateTest();
              }}
              placeholder="https://api.example.com/v1"
            />
            {LLM_PRESETS[preset].baseUrl && (
              <p className="text-xs text-muted-foreground">Filled in from the {LLM_PRESETS[preset].label} preset; edit only if your account uses a different endpoint.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-api-key">
              API key{LLM_PRESETS[preset].keyless ? ' (optional)' : ''}
            </Label>
            <Input
              id="llm-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                invalidateTest();
              }}
              placeholder={provider?.api_key_last4 ? `•••• ${provider.api_key_last4}` : 'sk-...'}
            />
            {provider && (
              <p className="text-xs text-muted-foreground">Leave blank to keep the current key.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="llm-default-model">Default model</Label>
              <Button type="button" variant="ghost" size="sm" onClick={handleLoadModels} disabled={loadingModels || !baseUrl || (!apiKey && !provider && !LLM_PRESETS[preset].keyless && preset !== 'openrouter')}>
                {loadingModels ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1" /> : <RefreshCw className="h-3.5 w-3.5 me-1" />}
                Load models
              </Button>
            </div>
            <ModelPicker
              id="llm-default-model"
              value={defaultModel}
              onChange={(v) => { setDefaultModel(v); invalidateTest(); }}
              curated={LLM_PRESETS[preset].models}
              live={liveModels}
            />
            {LLM_PRESETS[preset].freeNote && (
              <p className="text-xs text-muted-foreground">{LLM_PRESETS[preset].freeNote}</p>
            )}
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="px-0 hover:bg-transparent">
                <ChevronDown
                  className={`h-4 w-4 me-1 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
                />
                Advanced — model overrides
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="llm-override-english">English model</Label>
                <ModelPicker id="llm-override-english" allowInherit value={modelOverrides.english}
                  onChange={(v) => setModelOverrides((o) => ({ ...o, english: v }))}
                  curated={LLM_PRESETS[preset].models} live={liveModels} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="llm-override-fast">Fast model</Label>
                <ModelPicker id="llm-override-fast" allowInherit value={modelOverrides.fast}
                  onChange={(v) => setModelOverrides((o) => ({ ...o, fast: v }))}
                  curated={LLM_PRESETS[preset].models} live={liveModels} />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Test connection
            </Button>
            {testResult && (
              <span
                className={`flex items-center gap-1 text-sm ${
                  testResult.ok ? 'text-green-600' : 'text-destructive'
                }`}
              >
                {testResult.ok ? (
                  <>
                    <Check className="h-4 w-4" />
                    200 · {testResult.latencyMs} ms
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" />
                    {testResult.httpStatus} {testResult.error ?? 'failed'}
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleSaveWithoutTesting}
            disabled={saving || testing || !label.trim()}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50 self-start sm:self-auto"
          >
            Save without testing
          </button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => handleSave(false)} disabled={saveDisabled}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
